import React, { useState } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

export interface TextFieldComponentProps {
  label: string;
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  maxRows?: number;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  autoComplete?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  showPasswordToggle?: boolean;
}

/**
 * TextFieldComponent - Reusable Text Input Component
 * 
 * A standardized text field component that provides:
 * - Consistent styling and behavior across the application
 * - Built-in error handling and display
 * - Support for various input types (text, email, password, etc.)
 * - Flexible sizing and layout options
 * - Multiline support for text areas
 * - Icon support (start and end icons)
 * - Password visibility toggle for password fields
 */
export const TextFieldComponent: React.FC<TextFieldComponentProps> = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  fullWidth = true,
  placeholder,
  disabled = false,
  required = false,
  multiline = false,
  rows,
  maxRows,
  size = 'medium',
  variant = 'outlined',
  autoComplete,
  startIcon,
  endIcon,
  showPasswordToggle = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword(prev => !prev);
  };

  const actualType = showPasswordToggle ? (showPassword ? 'text' : 'password') : type;

  const getInputProps = () => {
    const props: any = {};
    
    if (startIcon || endIcon || showPasswordToggle) {
      props.startAdornment = startIcon ? (
        <InputAdornment position="start">
          {startIcon}
        </InputAdornment>
      ) : undefined;

      if (showPasswordToggle && type === 'password') {
        props.endAdornment = (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleTogglePassword}
              disabled={disabled}
              edge="end"
              size="small"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        );
      } else if (endIcon) {
        props.endAdornment = (
          <InputAdornment position="end">
            {endIcon}
          </InputAdornment>
        );
      }
    }

    return props;
  };

  // Handle date type with DatePicker
  if (type === 'date') {
    // Convert string value (YYYY-MM-DD) to Date object
    const dateValue = value ? new Date(value + 'T00:00:00') : null;

    const handleDateChange = (newDate: Date | null) => {
      if (newDate && !isNaN(newDate.getTime())) {
        // Convert to YYYY-MM-DD format for storage
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        onChange(`${year}-${month}-${day}`);
      } else {
        onChange('');
      }
    };

    return (
        <DatePicker
          label={label}
          value={dateValue}
          onChange={handleDateChange}
          disabled={disabled}
          format="dd/MM/yyyy"
          slotProps={{
            textField: {
              fullWidth,
              required,
              error: !!error,
              helperText: error,
              size,
              variant,
              sx: {
                "& .MuiInputLabel-root": {
                  "&.Mui-focused": {
                    color: "#2563eb",
                  },
                },
                "& .MuiInputBase-root": {
                  backgroundColor: "#f9fafb",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  color: "#111827",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: error ? 'error.main' : "#d1d5db",
                  },
                  "&:hover fieldset": {
                    borderColor: "#9ca3af",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563eb",
                    boxShadow: "0 0 0 1px #2563eb",
                  },
                },
              },
            },
          }}
        />
    );
  }

  return (
    <TextField
      label={label}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error}
      type={actualType}
      fullWidth={fullWidth}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      multiline={multiline}
      rows={rows}
      maxRows={maxRows}
      size={size}
      variant={variant}
      autoComplete={autoComplete}
      InputProps={getInputProps()}
      sx={{
        "& .MuiInputLabel-root": {
          // Center the label vertically
          transform: "translate(14px, 16px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
          "&.Mui-focused": {
            color: "#2563eb", // primary-600
          },
        },
        "& .MuiInputBase-root": {
          backgroundColor: "#f9fafb", // bg-gray-50
          borderRadius: "0.5rem",     // rounded-lg
          fontSize: "0.875rem",       // text-sm
          color: "#111827",           // text-gray-900
          padding: "0",               // reset internal
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: error ? 'error.main' : "#d1d5db",   // border-gray-300
          },
          "&:hover fieldset": {
            borderColor: "#9ca3af",   // slightly darker on hover
          },
          "&.Mui-focused fieldset": {
            borderColor: "#2563eb",   // primary-600
            boxShadow: "0 0 0 1px #2563eb", // focus:ring-primary-600
          },
          "& input": {
            padding: "0.625rem 0.75rem", // p-2.5
            height: "1.5rem", // Ensure consistent height
          },
        },
      }}
    />
  );
};

export default TextFieldComponent;